/**
 * otaBoot — cold-launch OTA update flow.
 *
 * The Capgo plugin runs in MANUAL mode (`autoUpdate: false` in
 * capacitor.config.ts) and this module drives the whole check → download →
 * apply sequence from JS instead. That is not "OTA off": the update still
 * happens on every cold launch, through the same plugin and the same
 * /ota/check endpoint. What changes is who decides *when*.
 *
 * Why manual rather than `autoUpdate: true` + `directUpdate: 'onLaunch'`,
 * which looks like the smaller change:
 *
 *  - Under autoUpdate the plugin calls backgroundDownload() on EVERY
 *    foreground (CapacitorUpdaterPlugin.appMovedToForeground), so a player
 *    returning to a live table pulls a bundle underneath the hand they are
 *    in. `directUpdate` only decides when a downloaded bundle is applied, not
 *    when it is fetched, so no value of it prevents that.
 *  - `directUpdate: 'onLaunch'` is gated by a single boolean that is consumed
 *    only when a direct update *completes*, not by any launch-window timer.
 *    A slow download on a cold start therefore lands as a set() + reload()
 *    minutes later, mid-hand.
 *
 * Manual mode also buys the things the acceptance criteria need and the
 * native loop cannot express: a hard timeout on the check, refusing to
 * re-download a bundle already flagged `error`, and a boot gate that resolves
 * exactly once.
 *
 * What we do NOT lose by turning autoUpdate off: rollback. The plugin arms
 * its notifyAppReady watchdog from _reload() and appMovedToForeground()
 * regardless of the flag, so a bundle that fails to boot still reverts and
 * autoDeleteFailed still applies. index.tsx's notifyAppReady() call must stay
 * where it is, outside this gate, for that to keep working.
 *
 * Failure policy. Two failures that look similar from in here are handled
 * deliberately differently:
 *
 *  - NO NETWORK AT LAUNCH: hold the player on a branded offline screen with a
 *    Retry button. Releasing the gate would drop them into a lobby that cannot
 *    open its socket, so there is nothing behind the gate worth releasing them
 *    into. This check runs before the OTA pin below, because being offline is
 *    not an update problem.
 *  - EVERYTHING ELSE (a slow or failed /ota/check, a failed, stalled or
 *    abandoned download, an apply that did not take): proceed on the bundle
 *    they already have, with no error and no stuck screen. The app works, it
 *    is just not on the newest bundle yet, and the next cold launch retries.
 *
 * There is deliberately no automatic retry loop or backoff for the offline
 * case. Retry is a button the player presses. A device with no network has
 * nothing to poll for, and a background backoff loop would spend battery to
 * eventually reach a screen the player is already looking at and can dismiss
 * themselves.
 *
 * Rollout contract: this whole flow stands down when the native config still
 * has autoUpdate enabled (see the guard in run()). capacitor.config.ts is
 * compiled into the native app, so a bundle carrying this code can land on a
 * shell built before the switch; there, the plugin keeps doing what it does
 * today and this module does nothing. That makes the JS safe to publish
 * ahead of the native build, and makes it impossible for the two loops to
 * ever run at once.
 */

import { Capacitor } from '@capacitor/core';
import { BundleInfo, CapacitorUpdater } from '@capgo/capacitor-updater';

/**
 * Hard cap on the version check (current() + getLatest() together, not each).
 *
 * The check is kicked off at module scope in index.tsx, before the first
 * render, so it overlaps React boot rather than queueing behind it: the
 * latency this ADDS is the check duration minus however long boot took, which
 * in practice is near zero.
 *
 * 1500ms rather than the 2500 this started at, so the worst case stays inside
 * the ~1s the acceptance criterion budgets even when boot is fast. The cost of
 * the tighter cap is that a genuinely slow /ota/check (a cold Lambda) skips
 * the update on THIS launch; the next cold start picks it up. That is a better
 * trade than making every launch wait.
 */
export const CHECK_TIMEOUT_MS = 1500;
/** No `download` progress event for this long means the transfer is wedged. */
export const DOWNLOAD_STALL_TIMEOUT_MS = 15_000;
/**
 * Total budget for the whole flow, after which the player goes to the lobby on
 * the bundle they already have.
 *
 * The stall watchdog above only fires on SILENCE, so a trickling connection
 * keeps producing progress events and holds the splash indefinitely with no
 * cap at all. The case that makes this a real problem rather than a tidiness
 * one is a tournament start: thousands of cold launches pulling a new bundle
 * over cellular, every one of them held on the splash while registration
 * closes.
 *
 * Abandoning a download is cheap here. Both platforms resume from a Range
 * header, so the next cold launch picks up where this one stopped rather than
 * starting over.
 */
export const TOTAL_BUDGET_MS = 25_000;
/**
 * set() reloads the WebView from inside the native call, so its promise often
 * never settles — we are torn down first. This is only here so a set() that
 * fails silently cannot hang the boot.
 */
export const APPLY_TIMEOUT_MS = 3000;
/**
 * How long to wait for the WebView to actually go away after set() has come
 * back, before concluding the apply did not take.
 *
 * This exists because of a measurement on a real device, not a theory. On
 * Android the plugin schedules the reload on the UI thread and returns
 * immediately, so set()'s promise RESOLVES while the current page is still
 * running. Timings from a Galaxy S24 (logcat):
 *
 *   T+0ms    CapgoUpdater: Reloading: /files/versions/QL1QkyCX7o
 *   T+2ms    our code resumes after `await set()`
 *   T+168ms  new bundle booted
 *
 * Without this wait we declared the apply failed in that 2ms window, released
 * the boot gate, let the app mount and start connecting, and then the page was
 * replaced underneath it — which the player sees as the launch screen
 * appearing twice in a row, once per page load.
 *
 * 3000ms is roughly 18x the measured gap, for a cold or low-end device. The
 * cost is only paid when an apply genuinely fails, which leaves the player on
 * "Installing update" that much longer before entering on the old bundle. The
 * total budget still caps the whole flow either way.
 */
export const APPLY_RELOAD_GRACE_MS = 3000;

/**
 * Build-time kill switch. The APK/IPA workflows use this to pin a manual test
 * build to the bundle it shipped with, so it is never overwritten by that
 * environment's current OTA bundle. It replaces the old
 * `sed 's/autoUpdate: true/autoUpdate: false/'` step, which stopped meaning
 * anything once the JS side started doing the fetching.
 */
const OTA_ENABLED = process.env.REACT_APP_OTA_ENABLED !== 'false';

/**
 * Greppable proof that a built bundle contains this updater.
 *
 * Manual mode puts the update mechanism INSIDE the payload it updates, so a
 * bundle published without this file is a one-way door: the device applies it,
 * loses the updater, and can never fetch anything again short of reinstalling
 * the app. Native autoUpdate is off, so there is no backstop.
 *
 * It is used in TWO places, closing two different holes:
 *
 *  1. deploy-web-app.yml greps the built output for this string and refuses to
 *     PUBLISH a bundle without it. That stops a rollback to a pre-updater tag
 *     from stranding everyone.
 *
 *  2. The publish workflow also stamps it into the manifest, and the check
 *     below refuses to APPLY a bundle whose manifest does not carry it. That
 *     stops the opposite case: a freshly installed native build, whose baked
 *     bundle DOES have the updater, downgrading itself onto an older published
 *     bundle that does not. Without this, shipping an APK/IPA before running a
 *     post-merge web deploy would strand every player who installed it, which
 *     is what happened on test1.
 *
 * Keep it in the emitted log below so it cannot be tree-shaken, and keep it in
 * lockstep with the workflow (there is a contract test asserting both agree).
 *
 * RETIRING THIS CHECK. It is a stand-in, not permanent. The clean replacement
 * is to compare versions by ORDERING instead of inequality and only apply a
 * bundle strictly newer than the running one, which prevents the downgrade
 * structurally. That needs the native build's baked bundle version and the
 * published bundle versions to sit on one monotonic line first: today a lower
 * environment deploys as 0.0.<run number> while an APK bakes 26.9.x, so
 * ordering would make those environments silently never update.
 *
 * When you do retire it, the order matters and only one direction is safe.
 * The check lives IN the bundle, so devices still running an older bundle are
 * the ones enforcing it:
 *
 *   1. Ship a client that no longer requires the marker, while the publish
 *      workflow KEEPS stamping it. Old devices accept that bundle because the
 *      marker is still present.
 *   2. Once those devices have moved on, stop stamping it.
 *
 * Stop stamping first and every device still on the old bundle stalls until
 * you republish with the field.
 */
export const OTA_UPDATER_SENTINEL = 'hijack-cold-launch-ota-v1';

export type OtaBootStatus = 'idle' | 'checking' | 'downloading' | 'applying' | 'offline' | 'done';

export type OtaBootState = {
  status: OtaBootStatus;
  /** 0-100 while downloading, 0 otherwise. */
  percent: number;
};

/** Why the flow ended. Surfaced for logs and tests, not for the player. */
export type OtaBootOutcome =
  | 'web' // not a native platform, nothing to do
  | 'disabled' // pinned build (REACT_APP_OTA_ENABLED=false)
  | 'native-autoupdate' // old shell still driving updates itself; stand down
  | 'up-to-date' // manifest matches the running bundle
  | 'error-bundle' // that version already exists locally and failed to boot
  | 'target-missing-updater' // the published bundle has no updater; refuse to apply
  | 'apply-failed' // the apply did not reload the WebView, so nothing changed
  | 'offline' // no network at all; hold the launch on the offline screen
  | 'failed'; // anything went wrong; continue on the current bundle

/** Injectable seams so the flow can be unit tested without a device. */
export type OtaBootDeps = {
  isNative: () => boolean;
  /**
   * Whether the device has a network at all. `navigator.onLine` rather than a
   * probe request: it reports the OS's own view of connectivity, which is the
   * "genuine lack of network" the offline screen exists for. A captive portal
   * reports online, so we proceed and let the ordinary OTA failure path
   * handle it, which is the behaviour we want — a portal is not the same
   * thing as airplane mode.
   */
  isOnline: () => boolean;
  /** Injectable so tests do not really wait out the reload grace. */
  sleep: (ms: number) => Promise<void>;
  updater: Pick<
    typeof CapacitorUpdater,
    'current' | 'getLatest' | 'list' | 'download' | 'set' | 'addListener' | 'isAutoUpdateEnabled'
  >;
};

const defaultDeps: OtaBootDeps = {
  isNative: () => Capacitor.isNativePlatform(),
  // `!== false` rather than a truthiness check: a WebView that does not
  // implement the property at all reads as undefined, and "we cannot tell"
  // must mean "carry on", never "block the app on an offline screen".
  isOnline: () => navigator.onLine !== false,
  sleep: (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
  updater: CapacitorUpdater
};

/**
 * Whether the cold-launch flow will do any work at all, answerable
 * synchronously. Lets the boot gate render its children on the first commit on
 * web, rather than holding for a microtask.
 *
 * Native only, and deliberately NOT also gated on OTA_ENABLED even though a
 * pinned build skips the update itself: the connectivity check in run() comes
 * before that pin, so a pinned build still has a verdict to wait for. Those
 * pinned builds are exactly the manual APK/IPA test builds this feature gets
 * verified on, and they would otherwise be the one shape of build where the
 * offline screen can never appear.
 */
export const isOtaBootActive = (deps: OtaBootDeps = defaultDeps): boolean => deps.isNative();

let state: OtaBootState = { status: 'idle', percent: 0 };
const listeners = new Set<(next: OtaBootState) => void>();
let running: Promise<OtaBootOutcome> | null = null;

const setState = (next: OtaBootState) => {
  state = next;
  listeners.forEach((listener) => listener(next));
};

export const getOtaBootState = (): OtaBootState => state;

export const subscribeOtaBoot = (listener: (next: OtaBootState) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const log = (message: string, ...rest: unknown[]) => console.log(`[OTA] ${message}`, ...rest);

/**
 * Resolve with `fallback` if `promise` has not settled in time. The underlying
 * work is NOT cancelled — the plugin gives us no handle to cancel with — it is
 * abandoned, so a late result is ignored rather than acted on.
 */
const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise.then((value) => {
      clearTimeout(timer);
      return value;
    }),
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), ms);
    })
  ]);
};

/**
 * Shared with run() so the apply step can see the clock.
 *
 * `expired` is the flag the download path checks. `deadlineAt` is there for a
 * sharper question the flag cannot answer: is there enough time LEFT to apply?
 * The native reload cannot be cancelled once started, so the apply has to be
 * refused up front rather than abandoned partway.
 */
type Budget = { expired: boolean; deadlineAt: number };

const run = async (deps: OtaBootDeps, budget: Budget): Promise<OtaBootOutcome> => {
  if (!deps.isNative()) return 'web';

  // Connectivity first, before the build pin and before any native call. The
  // app cannot function without a network at all: the lobby's socket will not
  // connect, so releasing the gate just moves the failure somewhere with no
  // way to act on it. Hold here instead, where there is a Retry.
  if (!deps.isOnline()) {
    log('no network at launch; holding the boot on the offline screen');
    setState({ status: 'offline', percent: 0 });
    return 'offline';
  }

  if (!OTA_ENABLED) {
    log('disabled for this build (REACT_APP_OTA_ENABLED=false)');
    return 'disabled';
  }

  // This bundle can reach a shell built BEFORE the switch to manual mode —
  // OTA ships the JS, but capacitor.config.ts is compiled into the native app,
  // so an existing install keeps `autoUpdate: true` until the player takes a
  // store update. On such a shell the plugin's own foreground loop is still
  // running, and two update loops at once is worse than either alone:
  //
  //  - Both would fetch the same bundle on a cold launch. The plugin's
  //    background path guards against a concurrent transfer, but the path a
  //    JS download() takes does not (CapgoUpdater.download mints a fresh
  //    bundle id and starts its own), so neither sees the other and the
  //    player pays for the zip twice.
  //  - The gate would half-work: a progress bar at launch, while resume-time
  //    downloads carry on underneath live gameplay anyway, since only the
  //    native config can stop those.
  //
  // So stand down completely and leave the old shell behaving exactly as it
  // does today. This runs before any network call, so an old shell does not
  // even pay for the duplicate version check. The new behaviour switches on by
  // itself once the player is on a build where autoUpdate is false.
  //
  // FAIL CLOSED when the check itself fails, which is a reversal of what this
  // did originally. The argument that changed it: a rejection here is not
  // random, it is CORRELATED with being on exactly the shell we are trying to
  // detect. isAutoUpdateEnabled is bridged by the native plugin, so a shell
  // old enough to predate it rejects with "not implemented" — and an old
  // shell is precisely the one still running its own update loop. On a shell
  // built from this branch the method is guaranteed present, so failing
  // closed costs those builds nothing.
  //
  // Standing down is also per-launch, not permanent: the memoised promise
  // lives for one page load, so the next cold start asks again.
  const autoUpdate = await deps.updater.isAutoUpdateEnabled().catch((err) => {
    log('cannot determine the native autoUpdate mode; standing down', err);
    return { enabled: true };
  });
  if (autoUpdate.enabled) {
    log('native autoUpdate is on (pre-manual-mode shell); leaving updates to the plugin');
    return 'native-autoupdate';
  }

  setState({ status: 'checking', percent: 0 });

  // One shared deadline for both calls rather than one each, so the worst case
  // stays CHECK_TIMEOUT_MS instead of double it.
  const checked = await withTimeout(
    Promise.all([deps.updater.current(), deps.updater.getLatest()]).catch((err) => {
      log('check failed', err);
      return null;
    }),
    CHECK_TIMEOUT_MS,
    null
  );

  if (!checked) {
    log('check timed out or failed; continuing on the current bundle');
    return 'failed';
  }

  const [current, latest] = checked;
  log(`current=${current.bundle.version} latest=${latest.version}`);

  // Both sides of this comparison are SOFTWARE (JS bundle) versions, never the
  // native shell version: capacitor.config.ts sets CapacitorUpdater.version to
  // the bundle version the build shipped with, so the builtin bundle reports
  // that rather than falling back to the native versionName.
  //
  // Compare by INEQUALITY, not semver ordering, which is what the native
  // autoUpdate path does too. Ordering would be wrong here anyway: the OTA
  // manifest version comes from the web deploy's app-v* line while a native
  // build's bundle version comes from its own android-v*/IPA line, so "greater
  // than" across them is not meaningful. Inequality also makes a deliberate
  // rollback of the published manifest apply, which is what we want.
  if (!latest.version || latest.version === current.bundle.version) return 'up-to-date';
  if (!latest.url) {
    log('manifest has no url; nothing to download');
    return 'failed';
  }

  // Refuse to apply a bundle that cannot update itself.
  //
  // Manual mode puts the updater inside the payload, so applying a bundle
  // built before this feature is a ONE-WAY DOOR: the device loses its only
  // updater and can never fetch anything again short of reinstalling. The
  // publish workflow verifies the bundle contains the updater and records that
  // in the manifest; no marker means we do not know, so we stay put.
  //
  // Carried in `comment` rather than a field of its own because iOS decodes
  // the manifest into a fixed struct (AppVersionDec) and drops unknown keys,
  // while Android passes everything through. `comment` and `link` are the only
  // free-form strings BOTH platforms accept.
  //
  // Failing this way is safe: the app keeps the bundle it has, which by
  // definition still has a working updater, and picks up the next properly
  // published bundle.
  if (latest.comment !== OTA_UPDATER_SENTINEL) {
    log(
      `published bundle ${latest.version} is not marked as carrying the updater; refusing to apply it`
    );
    return 'target-missing-updater';
  }

  // A bundle at this version may already be on the device. If it previously
  // failed to boot the plugin flagged it `error` and reverted — re-downloading
  // it would put the player straight back into the crash loop that rollback
  // just rescued them from.
  const known = await deps.updater
    .list()
    .then((res) => res.bundles.find((bundle) => bundle.version === latest.version))
    .catch(() => undefined);

  if (known?.status === 'error') {
    log(`bundle ${latest.version} is flagged error; staying on the current bundle`);
    return 'error-bundle';
  }

  let bundle: BundleInfo | undefined = known?.status === 'success' ? known : undefined;

  if (!bundle) {
    setState({ status: 'downloading', percent: 0 });
    bundle = await download(deps, latest.url, latest.version);
    if (!bundle) return 'failed';
  } else {
    log(`bundle ${latest.version} already downloaded; applying`);
  }

  if (budget.expired) {
    // The total budget ran out while downloading. Do NOT apply: a set() here
    // reloads the WebView, and by now the gate has released and the player is
    // in the lobby or at a table. The bundle stays on disk and applies on the
    // next cold launch instead.
    log('total budget expired before apply; leaving the bundle for the next launch');
    return 'failed';
  }

  // Not expired is not the same as having time to finish. The apply owns a
  // window of up to APPLY_TIMEOUT_MS + APPLY_RELOAD_GRACE_MS, and the native
  // reload cannot be cancelled once it starts. So if the budget would fire
  // inside that window, starting the apply is the worst of both worlds: the
  // budget releases the gate, the player reaches the lobby, and the reload
  // then lands underneath them.
  //
  // Refusing here costs nothing. The bundle is already on disk and applies on
  // the next cold launch, which is the same outcome as the expired branch
  // above, reached before doing any harm instead of after.
  const applyWindow = APPLY_TIMEOUT_MS + APPLY_RELOAD_GRACE_MS;
  if (budget.deadlineAt - Date.now() < applyWindow) {
    log(
      `only ${budget.deadlineAt - Date.now()}ms of the budget left and the apply needs ` +
        `${applyWindow}ms; leaving the bundle for the next launch`
    );
    return 'failed';
  }

  // Publishing 'applying' is also what hands the splash to the page on the
  // other side of the reload: OtaBootGate marks the continuation when it sees
  // this status, which it does before set() can tear the page down. The gate
  // owns that rather than this module because the gate owns the label the
  // next page has to keep showing.
  setState({ status: 'applying', percent: 100 });

  // set() calls _reload() internally, so there is no separate reload step.
  //
  // The important property: a SUCCESSFUL set() replaces the page, which stops
  // JS execution here. So reaching any line after this await means the WebView
  // was NOT torn down and the apply did not take effect, whether set()
  // resolved, rejected, or simply never reloaded within the timeout.
  //
  // This used to `return 'applying'` unconditionally, and the gate deliberately
  // does not release for that outcome, so a failed apply left the player on
  // "Installing update 100%" with no way out. It also recurred every launch:
  // the bundle was already on disk with status success, so the download was
  // skipped and the same set() failed again. The plugin's notifyAppReady
  // watchdog cannot rescue it either, because it is only armed by an actual
  // reload.
  //
  // Deliberately NOT calling setBundleError here. That would make list() report
  // the version as `error` on the next launch, which this flow refuses to
  // re-download, so a transient apply failure would permanently pin the device
  // to its current bundle. Retrying costs one cached set() per cold launch and
  // can recover; being pinned cannot.
  const applyCall = await withTimeout(
    deps.updater
      .set({ id: bundle.id })
      .then(() => 'resolved' as const)
      .catch((err) => {
        log('set failed', err);
        return 'rejected' as const;
      }),
    APPLY_TIMEOUT_MS,
    'unsettled' as const
  );

  // set() COMING BACK is not proof the apply failed, which is what this code
  // used to assume. On Android it resolves as soon as the reload is scheduled,
  // so we are still running for a couple of milliseconds while the page is on
  // its way out (see APPLY_RELOAD_GRACE_MS for the measured timings).
  //
  // So wait for the teardown we are expecting. If it comes, execution simply
  // stops here and the gate stays held through the reload, which is the point.
  // If it does not come, the apply really did not take and we fall through to
  // release the player onto the bundle they already have.
  //
  // A REJECTED set() is the one case worth skipping the wait for: the plugin
  // told us it failed, so there is no reload in flight to wait for.
  if (applyCall !== 'rejected') await deps.sleep(APPLY_RELOAD_GRACE_MS);

  // There is deliberately no success outcome to return. A successful apply
  // replaces the page, so this function never gets to return at all and the
  // gate stays held because its promise never settles. Reaching here means the
  // apply did not happen, and now means it after actually waiting for it.
  log('apply did not reload the WebView; continuing on the current bundle');
  return 'apply-failed';
};

/**
 * Download with progress, plus a watchdog for a transfer that stops producing
 * events. A retried or interrupted download resumes rather than restarting:
 * both platforms send a Range header for the bytes already on disk, so calling
 * download() again with the same url/version picks up where it left off.
 */
const download = async (
  deps: OtaBootDeps,
  url: string,
  version: string
): Promise<BundleInfo | undefined> => {
  let lastProgressAt = Date.now();

  const handle = await deps.updater
    .addListener('download', ({ percent }) => {
      lastProgressAt = Date.now();
      // Never let the bar go backwards, and hold the last 1% for the apply
      // step so it does not sit at 100% while the WebView is still reloading.
      setState({ status: 'downloading', percent: Math.min(99, Math.max(state.percent, percent)) });
    })
    .catch(() => null);
  // ONE interval, declared out here so `finally` can always clear it. It used
  // to be two — a watchdog setting a flag and a second poll inside the race
  // reading it — and the inner one was only cleared on the stall path, so
  // every successful download left a timer waking the WebView for the life of
  // the session.
  let watchdog: ReturnType<typeof setInterval> | undefined;

  try {
    const bundle = await Promise.race([
      deps.updater.download({ url, version }),
      new Promise<undefined>((resolve) => {
        watchdog = setInterval(() => {
          if (Date.now() - lastProgressAt > DOWNLOAD_STALL_TIMEOUT_MS) resolve(undefined);
        }, 1000);
      })
    ]);
    if (!bundle) log('download stalled; continuing on the current bundle');
    return bundle;
  } catch (err) {
    log('download failed', err);
    return undefined;
  } finally {
    if (watchdog) clearInterval(watchdog);
    handle?.remove();
  }
};

/**
 * Start the flow. Idempotent: repeat calls return the in-flight promise, so
 * index.tsx can kick this off at module scope and the gate can await the same
 * work rather than starting a second copy.
 */
export const startOtaBoot = (deps: OtaBootDeps = defaultDeps): Promise<OtaBootOutcome> => {
  if (running) return running;

  // A total cap on top of the per-step timeouts. `budget` is shared with run()
  // so that if the cap fires mid-download, the flow knows not to apply the
  // bundle afterwards: by then the gate has released and the player may be at
  // a table, and a late set() would reload the WebView underneath them.
  const budget: Budget = { expired: false, deadlineAt: Date.now() + TOTAL_BUDGET_MS };
  let budgetTimer: ReturnType<typeof setTimeout>;
  const capped = new Promise<OtaBootOutcome>((resolve) => {
    budgetTimer = setTimeout(() => {
      budget.expired = true;
      log(`total budget of ${TOTAL_BUDGET_MS}ms elapsed; continuing on the current bundle`);
      resolve('failed');
    }, TOTAL_BUDGET_MS);
  });

  running = Promise.race([run(deps, budget), capped])
    .finally(() => clearTimeout(budgetTimer))
    .catch((err): OtaBootOutcome => {
      log('unexpected failure', err);
      return 'failed';
    })
    .then((outcome) => {
      // The sentinel rides along here so it survives minification and
      // tree-shaking, and so an on-device log confirms which generation of the
      // mechanism is running.
      log(`outcome=${outcome} [${OTA_UPDATER_SENTINEL}]`);
      // 'offline' keeps its own status rather than being flattened to 'done'.
      // It is the one outcome the gate does not release for, and a gate that
      // mounts after the flow has already settled seeds itself from this
      // status — reporting 'done' there would release it into the dead lobby
      // the offline screen exists to prevent.
      setState(
        outcome === 'offline' ? { status: 'offline', percent: 0 } : { status: 'done', percent: 0 }
      );
      return outcome;
    });
  return running;
};

/**
 * Run the flow again from the top, for the offline screen's Retry button.
 *
 * Drops the memoised promise so startOtaBoot actually re-runs instead of
 * handing back the settled 'offline' result. Listeners are deliberately kept:
 * the gate subscribed once on mount and has to keep seeing state through the
 * retry, which is what lets a retry that finds a network hand over to the
 * download progress bar.
 */
export const retryOtaBoot = (deps: OtaBootDeps = defaultDeps): Promise<OtaBootOutcome> => {
  running = null;
  setState({ status: 'idle', percent: 0 });
  return startOtaBoot(deps);
};

/** Test-only: drop the module-level singleton between cases. */
export const resetOtaBootForTests = () => {
  running = null;
  state = { status: 'idle', percent: 0 };
  listeners.clear();
};
